import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  signal,
} from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { NubereaderExtensionService } from '../../services/extension/nubereader-extension';
import { MetadataService } from '../../services/metadata/metadata';
import { OfflineDownloadService } from '../../services/offline/offline-download';
import { OfflineSessionService } from '../../services/offline/offline-session';

type ExtensionStatus = 'checking' | 'installed' | 'missing' | 'unsupportedBrowser';

/** "More options → Offline reading": download the book for offline reading, or delete it. */
@Component({
  selector: 'nr-offline-reading-dialog',
  imports: [TranslatePipe, DecimalPipe, DatePipe],
  templateUrl: './offline-reading-dialog.html',
  styleUrl: './offline-reading-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class OfflineReadingDialogComponent {
  private static readonly BYTES_PER_MB = 1_048_576;
  private static readonly MAC_PLATFORM = /mac|iphone|ipad/iu;

  private readonly downloadService = inject(OfflineDownloadService);
  private readonly extension = inject(NubereaderExtensionService);
  private readonly metadataService = inject(MetadataService);
  private readonly offlineSession = inject(OfflineSessionService);

  private readonly otkId = computed(() => this.metadataService.metadata()?.otkId ?? '');

  readonly state = this.downloadService.state;
  readonly progress = this.downloadService.progress;
  readonly downloadedBook = this.downloadService.downloadedBook;
  readonly isOffline = this.offlineSession.isOffline;
  readonly extensionStatus = signal<ExtensionStatus>('checking');
  readonly sizeMb = computed(
    () => (this.metadataService.metadata()?.size ?? 0) / OfflineReadingDialogComponent.BYTES_PER_MB,
  );
  readonly loanEnd = computed(() => {
    const loanEnd = this.downloadedBook()?.loanEnd;
    return loanEnd !== undefined && Number.isFinite(loanEnd) ? loanEnd : undefined;
  });
  readonly downloadedElsewhere = this.downloadService.isDownloadedElsewhere;
  readonly bookmarkHintKey = OfflineReadingDialogComponent.MAC_PLATFORM.test(navigator.userAgent)
    ? 'OFFLINE.ADD_TO_BOOKMARKS_MAC'
    : 'OFFLINE.ADD_TO_BOOKMARKS_WIN';

  constructor() {
    void this.downloadService.refresh();
    void this.checkExtension();
  }

  close(): void {
    this.downloadService.isDialogOpen.set(false);
  }

  download(): void {
    void this.downloadService.download();
  }

  remove(): void {
    void this.downloadService.remove();
  }

  installExtension(): void {
    const url = this.extension.storeUrl(this.otkId());
    if (url) window.open(url, '_blank', 'noopener');
  }

  async checkExtension(): Promise<void> {
    if (!this.extension.isSupportedBrowser()) {
      this.extensionStatus.set('unsupportedBrowser');
      return;
    }
    this.extensionStatus.set('checking');
    const installed = await this.extension.isInstalled(this.otkId());
    this.extensionStatus.set(installed ? 'installed' : 'missing');
  }
}
